import React, { useState } from "react";
import { DataGridPro, GridColDef, GridRenderCellParams, GridRowParams, GridRowId } from "@mui/x-data-grid-pro";
import { Box, Slide } from "@mui/material";
import { Log } from "../../../types/LogTypes";
import { useLogger } from "../../../contexts/LoggerContext";
import { theme } from "../../../styles/Theme";
import { PlayDetails } from "./PlayDetails";
import { sideDisplayName } from "./Helpers/helpers";

interface LogTableProps {
  open: boolean;
}

export const LogTable: React.FC<LogTableProps> = ({ open }) => {
  const { gameLog } = useLogger();
  const [expandedRows, setExpandedRows] = useState<Set<GridRowId>>(new Set());
  
  const columns: GridColDef[] = [
    { field: "index", headerName: "#", width: 70 },
    {
      field: "play_type",
      headerName: "Side",
      width: 120,
      renderCell: (params: GridRenderCellParams<Log>) =>
          `${sideDisplayName(params.row.play_type)}`,
    },
    {
      field: "clubScore",
      headerName: "Club Score",
      width: 120,
      renderCell: (params: GridRenderCellParams<Log>) =>
        `${params.row.game_situation.club_score}`,
    },
    {
      field: "oppScore",
      headerName: "Opponent Score",
      width: 120,
      renderCell: (params: GridRenderCellParams<Log>) =>
        `${params.row.game_situation.opp_score}`,
    },
    {
      field: "qtr",
      headerName: "Qtr",
      width: 100,
      renderCell: (params: GridRenderCellParams<Log>) =>
        `${params.row.game_situation.quarter}`,
    },
    {
      field: "time",
      headerName: "Time",
      width: 100,
      renderCell: (params: GridRenderCellParams<Log>) =>
        `${params.row.game_situation.time_remaining}`,
    },
    {
      field: "down",
      headerName: "Down",
      width: 100,
      renderCell: (params: GridRenderCellParams<Log>) =>
        params.row.game_situation.down === -1 ? "-" : `${params.row.game_situation.down}`
    },
    {
      field: "distance",
      headerName: "Distance",
      width: 100,
      renderCell: (params: GridRenderCellParams<Log>) =>
        params.row.game_situation.distance === -1 ? "-" : `${params.row.game_situation.distance}`,
    },
    {
      field: "touchdown",
      headerName: "Touchdown",
      width: 120,
      renderCell: (params: GridRenderCellParams<Log>) =>
        `${params.row.touchdown ? "Yes" : ""}`,
    },
    {
      field: "safety",
      headerName: "Safety",
      width: 120,
      renderCell: (params: GridRenderCellParams<Log>) =>
        `${params.row.safety ? "Yes" : ""}`,
    },
  ];

  const rows = gameLog?.logs ? [...gameLog.logs].sort((a, b) => b.index - a.index) : [];

  const handleDetailPanelExpandedRowIdsChange = (newExpandedRows: Set<GridRowId>) => {
    setExpandedRows(newExpandedRows);
  };

  return (

    <Slide
      direction="up"
      in={open}
      mountOnEnter
      unmountOnExit
      style={{
        maxWidth: 1150,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          bottom: 0,
          height: "100%",
          bgcolor: theme.palette.background.paper,
          boxShadow: 3,
          zIndex: 1000,
          borderTop: 1,
          borderColor: "divider",
          top: 145,
          width: "100%",
        }}
      >
        <DataGridPro
          rows={rows}
          columns={columns}
          getRowId={(row) => row.id || row.index}
          autoHeight
          disableRowSelectionOnClick
          getDetailPanelContent={({ row }: GridRowParams<Log>) => (
            <PlayDetails log={row} />
          )}
          getDetailPanelHeight={() => 'auto'}
          detailPanelExpandedRowIds={expandedRows}
          onDetailPanelExpandedRowIdsChange={handleDetailPanelExpandedRowIdsChange}
          sx={{
            "& .MuiDataGrid-cell:focus": {
              outline: "none",
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "rgba(123, 45, 58, 0.9)",
              color: "white",
            },
            "& .MuiDataGrid-detailPanel": {
              backgroundColor: "rgba(244, 236, 236, 0.02)",
              borderTop: "1px solid rgba(123, 45, 58, 0.2)",
            },
            "& .MuiDataGrid-detailPanelToggleCell": {
              color: theme.palette.primary.main,
            },
            "& .MuiDataGrid-detailPanelToggleCell:hover": {
              backgroundColor: "rgba(123, 45, 58, 0.1)",
            },
          }}
        />
      </Box>
    </Slide>
  );
};
